import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword, updateMyProfile } from '../services/api';
import { formatPlanFeatureLabel } from '../utils/planFeatureLabels';
import { formatRegistrationTypeLabel } from '../utils/registrationTypes';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#163248] focus:border-transparent';

const TABS = [
  { id: 'profile', label: 'Edit Profile' },
  { id: 'password', label: 'Change Password' },
  { id: 'plan', label: 'Registration' },
];

const AccountSettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const planFeatures = useMemo(() => {
    const raw = user?.subscription_plan_features;
    if (!Array.isArray(raw)) return [];
    return [...raw].filter((f) => typeof f === 'string' && f.trim()).sort((a, b) => a.localeCompare(b));
  }, [user?.subscription_plan_features]);

  const planFeatureLabels = useMemo(
    () => planFeatures.map((f) => ({ key: f, label: formatPlanFeatureLabel(f) })),
    [planFeatures],
  );

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    const nameTrim = fullName.trim();
    const emailTrim = email.trim();
    if (!nameTrim) {
      setProfileError('Name cannot be empty.');
      return;
    }
    if (!emailTrim) {
      setProfileError('Email cannot be empty.');
      return;
    }
    const nameUnchanged = nameTrim === (user?.full_name || '').trim();
    const emailUnchanged = emailTrim === (user?.email || '').trim();
    if (nameUnchanged && emailUnchanged) {
      setProfileMessage('No changes to save.');
      return;
    }
    setProfileSubmitting(true);
    try {
      const payload = {};
      if (!nameUnchanged) payload.full_name = nameTrim;
      if (!emailUnchanged) payload.email = emailTrim;
      const res = await updateMyProfile(payload);
      if (res?.success && res.user) {
        updateUser(res.user);
        setProfileMessage('Profile updated successfully.');
      } else {
        setProfileError('Could not update profile.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setProfileError(typeof detail === 'string' ? detail : 'Could not update profile. Try again.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    if (!currentPassword.trim()) {
      setPasswordError('Enter your current password.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    setPasswordSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordMessage('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPasswordError(typeof detail === 'string' ? detail : 'Could not update password. Try again.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const tabBtnClass = (id) =>
    `min-w-0 flex-1 py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
      activeTab === id
        ? 'border-[#163248] text-[#163248] dark:border-primary-300 dark:text-primary-300'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
    }`;

  const registrationTypeLabel = formatRegistrationTypeLabel(user?.registration_type);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">User settings</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Username <span className="font-mono text-gray-800 dark:text-gray-200">{user?.username || '—'}</span> cannot be
        changed here.
      </p>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        <div
          className="grid grid-cols-3 border-b border-gray-200 dark:border-gray-700"
          role="tablist"
          aria-label="User settings sections"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`account-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`account-panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              className={tabBtnClass(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'profile' && (
            <div
              id="account-panel-profile"
              role="tabpanel"
              aria-labelledby="account-tab-profile"
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your name and email are shown in the app and used for account context.
              </p>

              {profileError && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
                  {profileError}
                </div>
              )}
              {profileMessage && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/40 border border-green-400 dark:border-green-700 text-green-800 dark:text-green-200 rounded text-sm">
                  {profileMessage}
                </div>
              )}

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="profile-full-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Full name
                  </label>
                  <input
                    id="profile-full-name"
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="profile-email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Email
                  </label>
                  <input
                    id="profile-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={profileSubmitting}
                  className="w-full bg-[#163248] hover:bg-[#1a3d5a] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {profileSubmitting ? 'Saving…' : 'Save profile'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div
              id="account-panel-password"
              role="tabpanel"
              aria-labelledby="account-tab-password"
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Enter your current password, then choose a new one.
              </p>

              {passwordError && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
                  {passwordError}
                </div>
              )}
              {passwordMessage && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/40 border border-green-400 dark:border-green-700 text-green-800 dark:text-green-200 rounded text-sm">
                  {passwordMessage}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="current-password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Current password
                  </label>
                  <input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="new-password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirm-password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Confirm new password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    className={inputClass}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={passwordSubmitting}
                  className="w-full bg-[#163248] hover:bg-[#1a3d5a] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordSubmitting ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'plan' && (
            <div
              id="account-panel-plan"
              role="tabpanel"
              aria-labelledby="account-tab-plan"
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your registration type determines whether you use the Corporate or Policy Maker experience.
                Platform page access is configured by your administrator.
              </p>

              <dl className="space-y-4 text-sm">
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 p-4">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Registration type
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {registrationTypeLabel}
                  </dd>
                </div>
              </dl>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Included page access</h3>
                {planFeatureLabels.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {planFeatureLabels.map(({ key, label }) => (
                      <li
                        key={key}
                        className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                      >
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No page-level restrictions are stored on your account (full access may apply), or your plan has not
                    been configured with feature flags yet.
                  </p>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
